import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "../auth/AuthProvider";
import { useGoals, type GoalWithStats } from "./api";
import { REQUIRED_GOALS, requiredGoalFor } from "./requiredGoals";
import { CheckInButton } from "../checkins/CheckInButton";
import { usePhotoUrl, useTodayCheckIns } from "../checkins/api";
import { useLikes } from "../feed/social";
import { Ring } from "../../components/Ring";
import { CheckIcon, FlameIcon, HeartIcon } from "../../components/icons";
import { Skeleton } from "../../components/ui";

const MONO = "font-['Geist_Mono',ui-monospace,monospace]";
const EYEBROW = `${MONO} text-[11px] uppercase tracking-[0.06em] text-[#8a8a94]`;

/** Black pill, 32px tall — the "Check in" button. */
const BUTTON =
  "!h-8 !rounded-full !bg-[#0a0a0b] !px-4 !py-0 !text-[13px] !font-medium !text-white hover:!bg-[#1f1f23] active:!bg-[#08080a]";

/** Today, per the Pact handoff (4a). */
export function GoalsPage() {
  const { userId } = useAuth();
  const goals = useGoals();
  const today = useTodayCheckIns(userId ?? undefined);
  const likes = useLikes();

  const mine = goals.data?.filter((goal) => goal.owner_id === userId && !goal.is_archived) ?? [];
  // The standing daily goals first, in their set order, then anything custom.
  const standing = REQUIRED_GOALS.map((def) =>
    mine.find((goal) => requiredGoalFor(goal) === def),
  ).filter((goal): goal is GoalWithStats => goal !== undefined);
  const custom = mine.filter((goal) => !requiredGoalFor(goal));
  const required = [...standing, ...custom];

  const todo = required.filter((goal) => !goal.stats?.period_complete);
  // Done goals sink below to-do, ordered by when they were completed.
  const done = required
    .filter((goal) => goal.stats?.period_complete)
    .map((goal) => ({ goal, checkIn: today.data?.find((entry) => entry.goal_id === goal.id) }))
    .sort((a, b) => (a.checkIn?.checked_in_at ?? "").localeCompare(b.checkIn?.checked_in_at ?? ""));

  const total = required.length;
  const left = todo.length;

  return (
    <div className="-mx-5 flex flex-col gap-[22px] px-5 pt-safe text-[#0a0a0b]">
      <header className="flex items-center justify-between gap-4 pt-5">
        <div className="flex flex-col gap-2">
          <span className={EYEBROW}>{format(new Date(), "EEE · MMM d")}</span>
          <h1 className="font-[inherit] text-[32px] font-semibold leading-none tracking-[-0.03em]">
            Today
          </h1>
          <p className="text-sm text-[#56565e]">
            {goals.isLoading
              ? " "
              : left === 0
                ? "All done. Nice."
                : `${left} little ${left === 1 ? "thing" : "things"} left`}
          </p>
        </div>

        <div role="img" aria-label={`${total - left} of ${total} done`}>
          <Ring fraction={total ? (total - left) / total : 0} size={84} stroke={7} disc>
            <div className="flex flex-col items-center gap-px">
              <span className={`${MONO} text-lg font-semibold`}>
                {total - left}/{total}
              </span>
              <span className="text-[10px] text-[#8a8a94]">done</span>
            </div>
          </Ring>
        </div>
      </header>

      {goals.isLoading ? (
        <div className="flex flex-col gap-2.5">
          <Skeleton className="h-[68px] !rounded-[20px]" />
          <Skeleton className="h-[68px] !rounded-[20px]" />
          <Skeleton className="h-[68px] !rounded-[20px]" />
        </div>
      ) : (
        <>
          {todo.length > 0 && (
            <section aria-labelledby="todo" className="flex flex-col gap-2.5">
              <h2 id="todo" className={`${EYEBROW} !font-normal`}>
                To do · {todo.length}
              </h2>
              <ul className="flex flex-col gap-2.5">
                {todo.map((goal) => (
                  <li
                    key={goal.id}
                    className="flex items-center gap-3 rounded-[20px] border border-[#ececef] bg-white py-3 pl-4 pr-3"
                  >
                    <span
                      aria-hidden
                      className="size-[22px] shrink-0 rounded-full border-[1.5px] border-[#dedee3]"
                    />
                    <Link
                      to={`/goals/${goal.id}`}
                      className="flex min-w-0 flex-1 flex-col gap-[3px] text-[#0a0a0b] no-underline"
                    >
                      <span className="truncate text-[15px] font-medium">{goal.title}</span>
                      <span
                        className={`${MONO} flex items-center gap-1 text-xs text-[#8a8a94]`}
                      >
                        <FlameIcon size={12} />
                        {streakLabel(goal.stats?.current_streak ?? 0)}
                      </span>
                    </Link>
                    <CheckInButton
                      goal={goal}
                      ownerId={userId!}
                      label="Check in"
                      className={BUTTON}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {done.length > 0 && (
            <section aria-labelledby="done" className="flex flex-col gap-2.5">
              <h2 id="done" className={`${EYEBROW} !font-normal`}>
                Done · {done.length}
              </h2>
              <ul className="flex flex-col gap-2.5">
                {done.map(({ goal, checkIn }) => (
                  <li key={goal.id}>
                    <DoneRow
                      goal={goal}
                      photoPath={checkIn?.photo_path ?? null}
                      time={checkIn?.checked_in_at ?? null}
                      reactions={
                        checkIn
                          ? (likes.data ?? []).filter((like) => like.check_in_id === checkIn.id)
                              .length
                          : 0
                      }
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

const streakLabel = (days: number) => (days > 0 ? `${days}-day streak` : "Start a streak");

function DoneRow({
  goal,
  photoPath,
  time,
  reactions,
}: {
  goal: GoalWithStats;
  photoPath: string | null;
  time: string | null;
  reactions: number;
}) {
  const photo = usePhotoUrl(photoPath);
  const streak = goal.stats?.current_streak ?? 0;

  return (
    <Link
      to={`/goals/${goal.id}`}
      className="flex items-center gap-3 rounded-[20px] border border-[#ececef] bg-white py-2 pl-2 pr-4 text-[#0a0a0b] no-underline"
    >
      <span className="relative size-[46px] shrink-0">
        {photo.data ? (
          <img src={photo.data} alt="" className="size-[46px] rounded-[14px] object-cover" />
        ) : (
          <span className="block size-[46px] rounded-[14px] bg-[#f3f3f5]" />
        )}
        <span
          aria-hidden
          className="absolute -bottom-1 -right-1 flex size-[19px] items-center justify-center rounded-full text-[oklch(0.35_0.06_160)] shadow-[0_0_0_2px_#fff]"
          style={{ background: "linear-gradient(135deg, oklch(0.88 0.08 175), oklch(0.82 0.1 155))" }}
        >
          <CheckIcon size={11} />
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="truncate text-[15px] font-medium text-[#56565e]">{goal.title}</span>
        <span className={`${MONO} text-xs text-[#8a8a94]`}>
          {time ? `${format(new Date(time), "h:mm a")} · ` : ""}
          {streak > 0 ? `${streak}-day streak` : "Streak started"}
        </span>
      </span>
      <span
        className={`${MONO} flex h-[26px] items-center gap-[5px] px-2.5 text-xs text-[#56565e]`}
      >
        <HeartIcon size={13} />
        {reactions}
      </span>
    </Link>
  );
}
